import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type Node } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, Badge, ErrorBox } from '../components/ui'
import { IconPlus } from '../components/icons'
import { FilterBar, FilterSelect } from '../components/Filters'
import { RowMenu } from '../components/RowMenu'

interface FirewallRule {
  id: number; node_id: string
  action: 'allow' | 'deny'
  cidr: string; port?: number; protocol: string
  comment?: string; created_at: string
}

export function FirewallPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [fNode, setFNode] = useState('')

  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const { data: rules = [] } = useQuery({
    queryKey: ['firewall-rules'],
    queryFn: async () => (await api.get<FirewallRule[]>('/firewall-rules')).data,
    refetchInterval: 10000,
  })
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n.name]))
  const filtered = useMemo(() => fNode ? rules.filter(x => x.node_id === fNode) : rules, [rules, fNode])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/firewall-rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['firewall-rules'] }),
  })

  return (
    <>
      <PageHeader
        title="Firewall (iptables)"
        subtitle="Правила allow/deny синхронизируются с iptables на нодах (цепочки beebro_allow / beebro_deny)."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить правило</Button>}
      />
      <FilterBar>
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
      </FilterBar>
      <Table<FirewallRule>
        rows={filtered}
        empty={rules.length === 0 ? 'Нет правил. Добавь чтобы блокировать/разрешать конкретные IP.' : 'Ничего не найдено.'}
        cols={[
          { key: 'a', label: 'Действие', render: (r) =>
            <Badge tone={r.action === 'allow' ? 'green' : 'red'}>{r.action === 'allow' ? 'разрешить' : 'запретить'}</Badge> },
          { key: 'c', label: 'CIDR', render: (r) => <span className="font-mono text-[12px] font-medium">{r.cidr}</span> },
          { key: 'p', label: 'Порт', render: (r) =>
            <span className="font-mono text-[12px]">{r.port ? `${r.port}/${r.protocol}` : `все ${r.protocol}`}</span> },
          { key: 'n', label: 'Нода', render: (r) => <span className="text-[13px] text-neutral-700">{nodeMap[r.node_id] || r.node_id.slice(0,8)}</span> },
          { key: 'cm', label: 'Комментарий', render: (r) => <span className="text-[12px] text-neutral-500">{r.comment || '—'}</span> },
          { key: 'act', label: '', w: '1%', render: (r) => (
            <div className="flex justify-end">
              <RowMenu items={[
                { label: 'Удалить', danger: true,
                  onClick: () => confirm(`Удалить правило ${r.action} ${r.cidr}?`) && del.mutate(r.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {addOpen && <AddFWModal nodes={nodes} onClose={() => setAddOpen(false)}
        onDone={() => { setAddOpen(false); qc.invalidateQueries({ queryKey: ['firewall-rules'] }) }} />}
    </>
  )
}

function AddFWModal({ nodes, onClose, onDone }: { nodes: Node[]; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ node_id: nodes[0]?.id || '', action: 'deny', cidr: '', port: 0, protocol: 'tcp', comment: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/firewall-rules', f)).data,
    onSuccess: onDone,
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить firewall-правило">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Нода">
          <Select value={f.node_id} onChange={e => setF({ ...f, node_id: e.target.value })}>
            {nodes.map(n => <option key={n.id} value={n.id}>{n.name}</option>)}
          </Select>
        </Field>
        <Field label="Действие">
          <Select value={f.action} onChange={e => setF({ ...f, action: e.target.value })}>
            <option value="deny">Запретить (DROP)</option>
            <option value="allow">Разрешить (ACCEPT)</option>
          </Select>
        </Field>
        <Field label="CIDR" hint="Пример: 1.2.3.4/32 или 10.0.0.0/24">
          <Input value={f.cidr} onChange={e => setF({ ...f, cidr: e.target.value })} placeholder="0.0.0.0/0" required className="font-mono" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Порт (0 = все)">
            <Input type="number" value={f.port} onChange={e => setF({ ...f, port: +e.target.value })} />
          </Field>
          <Field label="Протокол">
            <Select value={f.protocol} onChange={e => setF({ ...f, protocol: e.target.value })}>
              <option value="tcp">TCP</option>
              <option value="udp">UDP</option>
              <option value="all">Все</option>
            </Select>
          </Field>
        </div>
        <Field label="Комментарий">
          <Input value={f.comment} onChange={e => setF({ ...f, comment: e.target.value })} />
        </Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-3 border-t border-neutral-100 -mx-5 px-5 pt-4">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>{create.isPending ? '…' : 'Создать'}</Button>
        </div>
      </form>
    </Modal>
  )
}
